import React from "react";
import { ScreeningMatchStatus } from "../../../../types/cdd-records.types";

export const getScreeningStatusBadge = (
  status: ScreeningMatchStatus,
): { class: string; label: string } => {
  switch (status) {
    case "clear":
      return {
        class:
          "bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 border-emerald-300 shadow-sm",
        label: "Clear",
      };
    case "potential-match":
      return {
        class:
          "bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border-amber-300 shadow-sm",
        label: "Potential Match",
      };
    case "confirmed-match":
      return {
        class:
          "bg-gradient-to-r from-rose-100 to-red-100 text-rose-800 border-rose-300 shadow-sm",
        label: "Confirmed Match",
      };
    case "false-positive":
      return {
        class:
          "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 border-slate-300 shadow-sm",
        label: "False Positive",
      };
    default:
      return {
        class:
          "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 border-slate-300 shadow-sm",
        label: "Unknown",
      };
  }
};

export const screeningTypeLabels: Record<
  string,
  {
    label: string;
    icon: React.ReactElement;
    color: string;
    gradient: string;
    iconBg: string;
  }
> = {
  PEP: {
    label: "Politically Exposed Persons",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
    color: "violet",
    gradient: "from-violet-500 via-purple-500 to-fuchsia-500",
    iconBg:
      "bg-gradient-to-br from-violet-100 to-purple-100 text-violet-700 ring-2 ring-violet-200",
  },
  sanctions: {
    label: "Sanctions Screening",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    color: "rose",
    gradient: "from-rose-500 via-red-500 to-pink-500",
    iconBg:
      "bg-gradient-to-br from-rose-100 to-red-100 text-rose-700 ring-2 ring-rose-200",
  },
  "adverse-media": {
    label: "Adverse Media",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
        />
      </svg>
    ),
    color: "amber",
    gradient: "from-amber-500 via-orange-500 to-yellow-500",
    iconBg:
      "bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700 ring-2 ring-amber-200",
  },
  criminal: {
    label: "Criminal Records",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        />
      </svg>
    ),
    color: "sky",
    gradient: "from-sky-500 via-blue-500 to-indigo-500",
    iconBg:
      "bg-gradient-to-br from-sky-100 to-blue-100 text-sky-700 ring-2 ring-sky-200",
  },
  comprehensive: {
    label: "Comprehensive Screening",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
    color: "indigo",
    gradient: "from-indigo-500 via-purple-500 to-blue-500",
    iconBg:
      "bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700 ring-2 ring-indigo-200",
  },
};
